import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogOut, User, Users, Plus, LayoutDashboard, Scale, Luggage, Plane, Hotel, Phone, Settings, Calculator, Map, Briefcase, Info, ChevronDown, Menu, BookOpen, Compass, Clock, FileText, HelpCircle, Globe, Shield, Wallet, Car, Ticket, Wifi, ShieldCheck, Package, Headphones, MapPin, Star, Sun, Moon, Crown } from 'lucide-react';
import { Button } from './ui/button';
import { NotificationToggle } from './NotificationToggle';
import { useTheme } from '../context/ThemeContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Header = ({ transparent = false }) => {
  const { isAuthenticated, isAdmin, user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const txtCls = transparent ? 'text-white hover:bg-white/10' : isDark ? 'text-gray-300 hover:bg-white/10' : 'text-teal-700 hover:bg-teal-50';
  const displayName = user?.first_name || user?.email?.split('@')[0] || '';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        transparent ? 'bg-gray-900/70 backdrop-blur-md' : 'glass-header'
      }`}
      data-testid="app-header"
    >
      <div className="max-w-[1800px] mx-auto px-3 md:px-6 py-3 flex items-center justify-between">
        {/* Left: Logo + Nav */}
        <div className="flex items-center gap-1 md:gap-2">
          <Link to="/" className="flex items-center gap-2 hover:opacity-90 transition-opacity flex-shrink-0 mr-1" data-testid="logo-link">
            <img src="/logo.jpg" alt="Triple A Travel Solutions" className="w-9 h-9 rounded-lg object-contain" />
            <div className="flex flex-col">
              <span className={`font-serif text-base font-bold tracking-tight leading-tight ${transparent ? 'text-white' : isDark ? 'text-gray-100' : 'text-teal-800'}`}>Triple A</span>
              <span className={`text-[9px] font-sans font-medium tracking-widest uppercase leading-tight ${transparent ? 'text-amber-300' : isDark ? 'text-amber-400' : 'text-teal-600'}`}>Travel Solutions</span>
            </div>
          </Link>

          {/* Desktop nav (visible lg+) */}
          <nav className="hidden lg:flex items-center gap-0">

            {/* 1. About Us — standalone */}
            <Button variant="ghost" size="sm" onClick={() => navigate('/about')}
              className={`${txtCls} text-xs h-8 px-2 gap-1`} data-testid="header-about-btn">
              <Info className="w-3.5 h-3.5" /> About Us
            </Button>

            {/* 2. Services dropdown — Travel Services & Assisted Services */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className={`${txtCls} text-xs h-8 px-2 gap-1`} data-testid="header-services-dropdown">
                  <Briefcase className="w-3.5 h-3.5" /> Services <ChevronDown className="w-3 h-3 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                <DropdownMenuItem onClick={() => navigate('/travel-services')} className="cursor-pointer" data-testid="header-travel-services-item">
                  <Briefcase className="w-4 h-4 mr-2" /> Travel Services
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/assisted-services')} className="cursor-pointer" data-testid="header-assisted-services-item">
                  <Headphones className="w-4 h-4 mr-2" /> Assisted Services
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* 3. Plan dropdown — always visible */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className={`${txtCls} text-xs h-8 px-2 gap-1`} data-testid="header-plan-dropdown">
                  <Map className="w-3.5 h-3.5" /> Plan <ChevronDown className="w-3 h-3 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {isAuthenticated && (
                  <DropdownMenuItem onClick={() => navigate('/new-trip')} className="cursor-pointer" data-testid="header-new-trip-item">
                    <Plus className="w-4 h-4 mr-2" /> New Trip
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate('/templates')} className="cursor-pointer" data-testid="header-templates-item">
                  <Compass className="w-4 h-4 mr-2" /> Templates
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/calculate-budget')} className="cursor-pointer" data-testid="budget-nav-btn">
                  <Calculator className="w-4 h-4 mr-2" /> Budget Calculator
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/compare')} className="cursor-pointer" data-testid="compare-nav-btn">
                  <Scale className="w-4 h-4 mr-2" /> Compare Destinations
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/packing-list')} className="cursor-pointer" data-testid="packing-nav-btn">
                  <Luggage className="w-4 h-4 mr-2" /> Packing List
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/visa-check')} className="cursor-pointer" data-testid="visa-check-nav-btn">
                  <Globe className="w-4 h-4 mr-2" /> Visa Check
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* 4. Book dropdown — 7 affiliate services */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className={`${txtCls} text-xs h-8 px-2 gap-1`} data-testid="header-book-dropdown">
                  <Plane className="w-3.5 h-3.5" /> Book <ChevronDown className="w-3 h-3 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                <DropdownMenuItem onClick={() => navigate('/flights')} className="cursor-pointer" data-testid="header-flight-btn">
                  <Plane className="w-4 h-4 mr-2" /> Flights
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/hotels')} className="cursor-pointer" data-testid="header-hotel-btn">
                  <Hotel className="w-4 h-4 mr-2" /> Hotels
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/tour-packages')} className="cursor-pointer" data-testid="header-tour-btn">
                  <Map className="w-4 h-4 mr-2" /> Tour Packages
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/airport-transfers')} className="cursor-pointer" data-testid="header-airport-transfers-item">
                  <Car className="w-4 h-4 mr-2" /> Airport Transfers
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/local-trips')} className="cursor-pointer" data-testid="header-local-trips-item">
                  <MapPin className="w-4 h-4 mr-2" /> Local Trips
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/car-rentals')} className="cursor-pointer" data-testid="header-car-rentals-item">
                  <Car className="w-4 h-4 mr-2" /> Car Rentals
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/activities')} className="cursor-pointer" data-testid="header-activities-item">
                  <Ticket className="w-4 h-4 mr-2" /> Activities & Attractions
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/travel-insurance')} className="cursor-pointer" data-testid="header-insurance-item">
                  <ShieldCheck className="w-4 h-4 mr-2" /> Travel Insurance
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/flight-compensation')} className="cursor-pointer" data-testid="header-flight-comp-item">
                  <Plane className="w-4 h-4 mr-2" /> Flight Compensation
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/esim')} className="cursor-pointer" data-testid="header-esim-item">
                  <Wifi className="w-4 h-4 mr-2" /> eSIM & Connectivity
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/luggage-storage')} className="cursor-pointer" data-testid="header-luggage-item">
                  <Package className="w-4 h-4 mr-2" /> Luggage Storage
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* 5. Dashboard dropdown (auth only) */}
            {isAuthenticated && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className={`${txtCls} text-xs h-8 px-2 gap-1`} data-testid="header-dashboard-dropdown">
                    <LayoutDashboard className="w-3.5 h-3.5" /> Dashboard <ChevronDown className="w-3 h-3 opacity-60" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} className="cursor-pointer" data-testid="header-mytrips-item">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> My Trips
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/journal')} className="cursor-pointer" data-testid="header-journal-item">
                    <BookOpen className="w-4 h-4 mr-2" /> My Journal
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/stories')} className="cursor-pointer" data-testid="header-stories-item">
                    <Clock className="w-4 h-4 mr-2" /> Trip Story
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/blogs')} className="cursor-pointer" data-testid="header-blogs-item">
                    <FileText className="w-4 h-4 mr-2" /> Blog
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/budgets')} className="cursor-pointer" data-testid="header-budgets-item">
                    <Wallet className="w-4 h-4 mr-2" /> Budget Tracker
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* 7. Contact — standalone */}
            <Button variant="ghost" size="sm" onClick={() => navigate('/contact')}
              className={`${txtCls} text-xs h-8 px-2 gap-1`} data-testid="contact-nav-btn">
              <Phone className="w-3.5 h-3.5" /> Contact
            </Button>

            {/* 8. Reviews — standalone, public */}
            <Button variant="ghost" size="sm" onClick={() => navigate('/reviews')}
              className={`${txtCls} text-xs h-8 px-2 gap-1`} data-testid="header-reviews-btn">
              <Star className="w-3.5 h-3.5" /> Reviews
            </Button>

            {/* 9. FAQ — standalone */}
            <Button variant="ghost" size="sm" onClick={() => navigate('/faq')}
              className={`${txtCls} text-xs h-8 px-2 gap-1`} data-testid="faq-nav-btn">
              <HelpCircle className="w-3.5 h-3.5" /> FAQ
            </Button>

            {/* 10. Membership — admin only until Stripe is live */}
            {isAdmin && (
            <Button variant="ghost" size="sm" onClick={() => navigate('/membership')}
              className={`${txtCls} text-xs h-8 px-2 gap-1`} data-testid="membership-nav-btn">
              <Crown className="w-3.5 h-3.5" /> Membership
            </Button>
            )}

            {/* 11. Admin — admin only */}
            {isAdmin && (
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}
                className={`${txtCls} text-xs h-8 px-2 gap-1`} data-testid="admin-dashboard-nav-btn">
                <Shield className="w-3.5 h-3.5" /> Admin
              </Button>
            )}
          </nav>
        </div>

        {/* Right: Notification + User menu (always visible) */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Dark mode toggle */}
          <Button variant="ghost" size="sm" onClick={toggleTheme}
            className={`${txtCls} h-8 w-8 p-0`} data-testid="theme-toggle-btn"
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>

          {isAuthenticated ? (
            <>
              <NotificationToggle variant="icon" />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className={`flex items-center gap-1.5 h-8 px-3 ${transparent ? 'border-white/40 text-white hover:bg-white/10' : isDark ? 'border-gray-600 text-gray-300 hover:bg-white/10' : 'border-teal-300 text-teal-700 hover:bg-teal-50'}`} data-testid="user-menu-btn">
                    <User className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline max-w-[100px] truncate text-xs">{displayName}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {/* Mobile-only nav: About Us → Travel Services → Plan → Book → Dashboard → Assisted Services → Contact → FAQ → Admin */}
                  <DropdownMenuItem onClick={() => navigate('/about')} className="cursor-pointer lg:hidden" data-testid="about-menu-item">
                    <Info className="w-4 h-4 mr-2" /> About Us
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/travel-services')} className="cursor-pointer lg:hidden" data-testid="travel-services-menu-item">
                    <Briefcase className="w-4 h-4 mr-2" /> Travel Services
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="lg:hidden" />
                  {/* Plan section */}
                  <DropdownMenuItem onClick={() => navigate('/new-trip')} className="cursor-pointer lg:hidden" data-testid="new-trip-menu-item">
                    <Plus className="w-4 h-4 mr-2" /> New Trip
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/templates')} className="cursor-pointer lg:hidden" data-testid="templates-menu-item">
                    <Compass className="w-4 h-4 mr-2" /> Templates
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/calculate-budget')} className="cursor-pointer lg:hidden" data-testid="budget-menu-item">
                    <Calculator className="w-4 h-4 mr-2" /> Budget Calculator
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/compare')} className="cursor-pointer lg:hidden" data-testid="compare-menu-item">
                    <Scale className="w-4 h-4 mr-2" /> Compare
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/packing-list')} className="cursor-pointer lg:hidden" data-testid="packing-list-menu-item">
                    <Luggage className="w-4 h-4 mr-2" /> Packing List
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/visa-check')} className="cursor-pointer lg:hidden" data-testid="visa-check-menu-item">
                    <Globe className="w-4 h-4 mr-2" /> Visa Check
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="lg:hidden" />
                  {/* Book section */}
                  <DropdownMenuItem onClick={() => navigate('/flights')} className="cursor-pointer lg:hidden" data-testid="flight-menu-item">
                    <Plane className="w-4 h-4 mr-2" /> Flights
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/hotels')} className="cursor-pointer lg:hidden" data-testid="hotel-menu-item">
                    <Hotel className="w-4 h-4 mr-2" /> Hotels
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/tour-packages')} className="cursor-pointer lg:hidden" data-testid="tour-menu-item">
                    <Map className="w-4 h-4 mr-2" /> Tour Packages
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/airport-transfers')} className="cursor-pointer lg:hidden" data-testid="airport-transfers-menu-item">
                    <Car className="w-4 h-4 mr-2" /> Airport Transfers
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/local-trips')} className="cursor-pointer lg:hidden" data-testid="local-trips-menu-item">
                    <MapPin className="w-4 h-4 mr-2" /> Local Trips
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/car-rentals')} className="cursor-pointer lg:hidden" data-testid="car-rentals-menu-item">
                    <Car className="w-4 h-4 mr-2" /> Car Rentals
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/activities')} className="cursor-pointer lg:hidden" data-testid="activities-menu-item">
                    <Ticket className="w-4 h-4 mr-2" /> Activities
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/travel-insurance')} className="cursor-pointer lg:hidden" data-testid="insurance-menu-item">
                    <ShieldCheck className="w-4 h-4 mr-2" /> Travel Insurance
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/flight-compensation')} className="cursor-pointer lg:hidden" data-testid="flight-comp-menu-item">
                    <Plane className="w-4 h-4 mr-2" /> Flight Compensation
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/esim')} className="cursor-pointer lg:hidden" data-testid="esim-menu-item">
                    <Wifi className="w-4 h-4 mr-2" /> eSIM
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/luggage-storage')} className="cursor-pointer lg:hidden" data-testid="luggage-menu-item">
                    <Package className="w-4 h-4 mr-2" /> Luggage Storage
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="lg:hidden" />
                  {/* Dashboard section */}
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} className="cursor-pointer lg:hidden" data-testid="dashboard-menu-item">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> My Trips
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/journal')} className="cursor-pointer lg:hidden" data-testid="journal-menu-item">
                    <BookOpen className="w-4 h-4 mr-2" /> My Journal
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/stories')} className="cursor-pointer lg:hidden" data-testid="stories-menu-item">
                    <Clock className="w-4 h-4 mr-2" /> Trip Story
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/blogs')} className="cursor-pointer lg:hidden" data-testid="blogs-menu-item">
                    <FileText className="w-4 h-4 mr-2" /> Blog
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/budgets')} className="cursor-pointer lg:hidden" data-testid="budgets-menu-item">
                    <Wallet className="w-4 h-4 mr-2" /> Budget Tracker
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="lg:hidden" />
                  {/* Assisted Services / Contact / FAQ */}
                  <DropdownMenuItem onClick={() => navigate('/assisted-services')} className="cursor-pointer lg:hidden" data-testid="assisted-services-menu-item">
                    <Headphones className="w-4 h-4 mr-2" /> Assisted Services
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/contact')} className="cursor-pointer lg:hidden" data-testid="contact-menu-item">
                    <Phone className="w-4 h-4 mr-2" /> Contact
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/reviews')} className="cursor-pointer lg:hidden" data-testid="reviews-menu-item">
                    <Star className="w-4 h-4 mr-2" /> Reviews
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/faq')} className="cursor-pointer lg:hidden" data-testid="faq-menu-item">
                    <HelpCircle className="w-4 h-4 mr-2" /> FAQ
                  </DropdownMenuItem>
                  {isAdmin && (
                  <DropdownMenuItem onClick={() => navigate('/membership')} className="cursor-pointer lg:hidden" data-testid="membership-menu-item">
                    <Crown className="w-4 h-4 mr-2" /> Membership
                  </DropdownMenuItem>
                  )}
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer lg:hidden" data-testid="admin-menu-item">
                      <Shield className="w-4 h-4 mr-2" /> Admin Dashboard
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="lg:hidden" />
                  {/* Always visible in dropdown */}
                  <DropdownMenuItem onClick={() => navigate('/referrals')} className="cursor-pointer" data-testid="referrals-menu-item">
                    <Users className="w-4 h-4 mr-2" /> Referrals
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/clients')} className="cursor-pointer" data-testid="clients-menu-item">
                    <Briefcase className="w-4 h-4 mr-2" /> Clients
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer" data-testid="profile-menu-item">
                    <Settings className="w-4 h-4 mr-2" /> Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive focus:text-destructive" data-testid="logout-menu-item">
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              {/* Mobile hamburger for unauthenticated */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className={`lg:hidden ${txtCls} h-8 px-2`}>
                    <Menu className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem onClick={() => navigate('/about')} className="cursor-pointer">
                    <Info className="w-4 h-4 mr-2" /> About Us
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/travel-services')} className="cursor-pointer">
                    <Briefcase className="w-4 h-4 mr-2" /> Travel Services
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/templates')} className="cursor-pointer">
                    <Compass className="w-4 h-4 mr-2" /> Templates
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/flights')} className="cursor-pointer">
                    <Plane className="w-4 h-4 mr-2" /> Flights
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/hotels')} className="cursor-pointer">
                    <Hotel className="w-4 h-4 mr-2" /> Hotels
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/tour-packages')} className="cursor-pointer">
                    <Map className="w-4 h-4 mr-2" /> Tour Packages
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/airport-transfers')} className="cursor-pointer">
                    <Car className="w-4 h-4 mr-2" /> Airport Transfers
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/local-trips')} className="cursor-pointer">
                    <MapPin className="w-4 h-4 mr-2" /> Local Trips
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/car-rentals')} className="cursor-pointer">
                    <Car className="w-4 h-4 mr-2" /> Car Rentals
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/activities')} className="cursor-pointer">
                    <Ticket className="w-4 h-4 mr-2" /> Activities
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/travel-insurance')} className="cursor-pointer">
                    <ShieldCheck className="w-4 h-4 mr-2" /> Insurance
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/flight-compensation')} className="cursor-pointer">
                    <Plane className="w-4 h-4 mr-2" /> Flight Compensation
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/esim')} className="cursor-pointer">
                    <Wifi className="w-4 h-4 mr-2" /> eSIM
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/luggage-storage')} className="cursor-pointer">
                    <Package className="w-4 h-4 mr-2" /> Luggage Storage
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/assisted-services')} className="cursor-pointer">
                    <Headphones className="w-4 h-4 mr-2" /> Assisted Services
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/contact')} className="cursor-pointer">
                    <Phone className="w-4 h-4 mr-2" /> Contact
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/reviews')} className="cursor-pointer">
                    <Star className="w-4 h-4 mr-2" /> Reviews
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/faq')} className="cursor-pointer">
                    <HelpCircle className="w-4 h-4 mr-2" /> FAQ
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="ghost" size="sm" className={`${txtCls} text-xs h-8`} onClick={() => navigate('/auth')} data-testid="login-btn">Log in</Button>
              <Button size="sm" className="bg-gradient-to-r from-teal-600 to-teal-700 text-white hover:from-teal-700 hover:to-teal-800 rounded-full px-5 text-xs h-8"
                onClick={() => navigate('/auth?mode=register')} data-testid="signup-btn">Get Started</Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
